import { twMerge } from "tailwind-merge";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useDismissableLayer } from "../DismissableLayer/useDismissableLayer";
import { createPortal } from "react-dom";

const Popover = ({
  align = "start",
  children,
  containerClassName,
  contentClassName,
  hideTriggerWhenOpen = false,
  isOpen,
  offset = 8,
  onClose,
  side = "bottom",
  trigger,
}) => {
  const triggerWrapRef = useRef(null);
  const contentRef = useRef(null);
  const [pos, setPos] = useState(null);

  // ✅ Only topmost layer dismisses (portal-safe)
  useDismissableLayer({
    enabled: isOpen,
    refs: [triggerWrapRef, contentRef],
    onDismiss: () => onClose?.(),
    onEscape: () => onClose?.(),
  });

  const compute = () => {
    const triggerEl = triggerWrapRef.current;
    const contentEl = contentRef.current;
    if (!triggerEl || !contentEl) return;

    const t = triggerEl.getBoundingClientRect();
    const c = contentEl.getBoundingClientRect();

    const top = side === "top" ? t.top - c.height - offset : t.bottom + offset;

    let left;
    if (align === "end") left = t.right - c.width;
    else if (align === "center") left = t.left + (t.width - c.width) / 2;
    else left = t.left;

    // keep inside viewport a bit
    const pad = 8;
    left = Math.max(pad, Math.min(left, window.innerWidth - c.width - pad));

    setPos({
      top: Math.max(pad, top),
      left,
      width: t.width,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    compute();
  }, [isOpen, side, align, offset]);

  useEffect(() => {
    if (!isOpen) return;
    const onScroll = () => compute();
    const onResize = () => compute();

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [isOpen]);

  return (
    <div
      className={twMerge("relative", containerClassName)}
      ref={triggerWrapRef}
    >
      {hideTriggerWhenOpen && isOpen ? null : trigger}

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={contentRef}
              role="dialog"
              aria-modal="false"
              initial={{ opacity: 0, scale: 0.98, y: side === "top" ? 4 : -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: side === "top" ? 4 : -4 }}
              transition={{ duration: 0.12, ease: "easeOut" }}
              className={twMerge("fixed z-50", contentClassName)}
              style={
                pos
                  ? { top: pos.top, left: pos.left }
                  : { top: -9999, left: -9999 }
              }
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default Popover;
